import { Globe2, Search, Check, Loader2, ChevronDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Input, Button } from '@/components/design-system';
import {
  COLOR_SCALE,
  FONT_WEIGHTS,
  GAP_SCALE,
  MARGIN_LEFT,
  PADDING_LEFT,
  PADDING_Y,
  RADIUS_SCALE,
  SIZE_SCALE,
  SPACE_Y_SCALE,
  TYPE_SCALE,
  DIVIDE,
  DISPLAY,
  FLEX,
  TEXT_TRANSFORM,
} from '@/components/design-system/tokens';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { useCurrency } from '@/hooks/useCurrency';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { ensureLocale, formatDateTime } from '@/utils';

interface CurrencySelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CurrencySwitcherProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  hideLabel?: boolean;
  variant?: 'default' | 'ghost' | 'outline' | 'secondary' | 'primary';
  ariaLabel?: string;
  buttonProps?: React.ComponentProps<typeof Button>;
}

const SAMPLE_AMOUNT = 100;

const getDisplayNames = (locale: string) => {
  if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
    try {
      return new Intl.DisplayNames([locale], { type: 'currency' });
    } catch {
      // Intl.DisplayNames not available for locale
    }
  }
  return null;
};

function CurrencySelectorDialog({ open, onOpenChange }: CurrencySelectorDialogProps) {
  const {
    availableCurrencies,
    selectedCurrency,
    setSelectedCurrency,
    formatAmount,
    loading,
    lastUpdated,
    baseCurrency,
  } = useCurrency();
  const { t, currentLanguage } = useTranslation();
  const [search, setSearch] = useState('');

  const resolvedLocale = useMemo(
    () => ensureLocale(currentLanguage, 'en') ?? 'en',
    [currentLanguage]
  );

  const displayNames = useMemo(() => getDisplayNames(resolvedLocale), [resolvedLocale]);

  const filteredCurrencies = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return availableCurrencies;
    }

    return availableCurrencies.filter(code => {
      if (!code) return false;
      if (code.toLowerCase().includes(query)) return true;

      const displayName = displayNames?.of(code);
      if (displayName?.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  }, [availableCurrencies, displayNames, search]);

  const handleSelect = (code: string) => {
    setSelectedCurrency(code);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className={`flex items-center ${GAP_SCALE['2']}`}>
            <Globe2 className={`${SIZE_SCALE['5']} ${COLOR_SCALE.primary}`} />
            {t('currency', 'choose_currency', 'Choose your currency')}
          </DialogTitle>
          <DialogDescription className={`${TYPE_SCALE.sm} text-muted-foreground`}>
            {t(
              'currency',
              'choose_currency_hint',
              'Select a currency to convert all treatment prices instantly.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className={`${SPACE_Y_SCALE['4']}`}>
          <div className="relative">
            <Search
              className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2`}
            />
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t('currency', 'search_placeholder', 'Search currency or country')}
              className={`${PADDING_LEFT['8']}`}
            />
          </div>

          <div className={`border ${RADIUS_SCALE.lg}`}>
            {loading ? (
              <div
                className={`flex items-center justify-center ${PADDING_Y['10']} text-sm text-muted-foreground ${GAP_SCALE['2']}`}
              >
                <Loader2 className={`${SIZE_SCALE.w4} ${SIZE_SCALE['4']} animate-spin`} />
                {t('currency', 'loading_rates', 'Loading exchange rates...')}
              </div>
            ) : filteredCurrencies.length === 0 ? (
              <div
                className={`${PADDING_Y['8']} text-center ${TYPE_SCALE.sm} text-muted-foreground`}
              >
                {t(
                  'currency',
                  'no_results',
                  'No currencies match your search. Try another code or country.'
                )}
              </div>
            ) : (
              <ScrollArea className={`${SIZE_SCALE['80']}`}>
                <div className={`${DIVIDE.y.thin} ${DIVIDE.color.gray[200]}`}>
                  {filteredCurrencies.map(code => {
                    const formattedSample = formatAmount(SAMPLE_AMOUNT, {
                      currency: code,
                    });
                    if (!formattedSample) {
                      return null;
                    }

                    const displayName = displayNames?.of(code) ?? code;
                    const isActive = code === selectedCurrency;

                    return (
                      <button
                        key={code}
                        onClick={() => handleSelect(code)}
                        className={cn(
                          'w-full flex items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                          isActive && 'bg-blue-50/60'
                        )}
                      >
                        <div className={`flex flex-col ${GAP_SCALE['0.5']}`}>
                          <span className={`text-sm ${FONT_WEIGHTS.semibold} text-foreground`}>
                            {code}
                          </span>
                          <span className={`${TYPE_SCALE.xs} text-muted-foreground`}>
                            {displayName}
                          </span>
                        </div>
                        <div className={`flex items-center ${GAP_SCALE['3']}`}>
                          <span
                            className={`text-sm ${FONT_WEIGHTS.medium} text-foreground whitespace-nowrap`}
                          >
                            {formattedSample}
                            <span
                              className={`${MARGIN_LEFT['1']} ${TYPE_SCALE.xs} text-muted-foreground`}
                            >
                              ({SAMPLE_AMOUNT} {baseCurrency})
                            </span>
                          </span>
                          {isActive && (
                            <Check className={`${SIZE_SCALE['4']} ${COLOR_SCALE.primary}`} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>

          {lastUpdated && (
            <p className={`${TYPE_SCALE.xs} text-muted-foreground text-right`}>
              {t('currency', 'last_updated', 'Rates last updated')}:{' '}
              {formatDateTime(lastUpdated, { locale: resolvedLocale })}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CurrencySwitcher({
  className,
  size = 'sm',
  hideLabel = false,
  variant = 'ghost',
  ariaLabel,
  buttonProps = {},
}: CurrencySwitcherProps) {
  const { selectedCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const label = hideLabel
    ? selectedCurrency
    : t('currency', 'current_currency', '{{currency}}', {
        currency: selectedCurrency,
      });
  const accessibleLabel = ariaLabel ?? t('currency', 'change_currency', 'Change currency');

  return (
    <>
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={() => setOpen(true)}
        className={cn(
          DISPLAY.inlineFlex,
          FLEX.flexItemsCenter,
          GAP_SCALE['1.5'],
          TYPE_SCALE.xs,
          FONT_WEIGHTS.semibold,
          TEXT_TRANSFORM.uppercase,
          'tracking-wide',
          className
        )}
        aria-label={accessibleLabel}
        {...buttonProps}
      >
        <Globe2 className={`${SIZE_SCALE['3.5']}`} />
        <span>{label}</span>
        <ChevronDown className={`${SIZE_SCALE['3']} opacity-80`} />
      </Button>
      <CurrencySelectorDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export default CurrencySwitcher;

export { CurrencySelectorDialog };
